import { useState, useContext } from "react";
import { AlertContext } from "../../../context/AlertContext";
import { domain, fonts } from "../../../consts";
import { getCloudflareImageUrl } from "../../../utils/imageOptimization";

const textures = [
  { id: "none", url: "", name: "Bez teksture" },
  { id: "texture1", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture1.jpg", name: "Mramor" },
  { id: "texture2", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture2.jpg", name: "Papir" },
  { id: "texture3", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture3.jpg", name: "Platno" },
  { id: "texture4", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture4.jpg", name: "Drvo" },
  { id: "texture5", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture5.jpg", name: "Cvijeće" },
  { id: "texture6", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture6.jpg", name: "Listovi" },
  { id: "texture7", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture7.jpg", name: "Vintage" },
  { id: "texture8", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture8.jpg", name: "Elegantno" },
  { id: "texture15", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture15.jpg", name: "Ruže" },
  { id: "texture20", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture20.jpg", name: "Zlato" },
  { id: "texture25", url: "https://bucket.pozivnica.hr/ui-bucket/textures/texture25.jpg", name: "Rustic" },
];

const colorPresets = [
  { name: "Klasično", bg: "#F5F5F0", highlight: "#D4AF37", text: "#FFFFFF" },
  { name: "Romantično", bg: "#FDF2F8", highlight: "#EC4899", text: "#831843" },
  { name: "Elegantno", bg: "#1F2937", highlight: "#F59E0B", text: "#FFFFFF" },
  { name: "Prirodno", bg: "#ECFDF5", highlight: "#059669", text: "#064E3B" },
  { name: "Moderno", bg: "#EFF6FF", highlight: "#3B82F6", text: "#1E3A8A" },
  { name: "Vintage", bg: "#FEF3C7", highlight: "#92400E", text: "#451A03" },
];

const StepCustomizeDesign = ({ formData, updateFormData }) => {
  const { handleAlert } = useContext(AlertContext);
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [activeTab, setActiveTab] = useState("background");

  const uploadImageToS3 = async (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const fileName = `${crypto.randomUUID()}.${fileExtension}`;

    const presignedResponse = await fetch(`${domain}/generate-presigned-url`, {
      credentials: "include",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName, fileType: file.type }),
    });

    if (!presignedResponse.ok) throw new Error("Failed to get presigned URL");

    const { url: presignedUrl, publicUrl } = await presignedResponse.json();

    const uploadResponse = await fetch(presignedUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });

    if (!uploadResponse.ok) throw new Error("Failed to upload image");

    return publicUrl;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingHeader(true);
    try {
      const imageUrl = await uploadImageToS3(file);
      updateFormData({ headerImage: "https://" + imageUrl });
      handleAlert("Slika uspješno učitana!", "success");
    } catch (error) {
      handleAlert("Greška pri učitavanju slike. Pokušajte ponovno.", "error");
    } finally {
      setIsUploadingHeader(false);
    }
  };

  const applyColorPreset = (preset) => {
    updateFormData({
      bgColor: preset.bg,
      highlightColor: preset.highlight,
      titleColor: preset.text,
      dateColor: preset.text,
    });
  };

  const tabs = [
    { id: "background", label: "Pozadina" },
    { id: "colors", label: "Boje" },
    { id: "typography", label: "Fontovi" },
    { id: "image", label: "Slika" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex border-b border-gray-200 -mx-6 px-6 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-teal-500 text-teal-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "background" && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Pozadinska tekstura
            </label>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {textures.map((texture) => (
                <button
                  key={texture.id}
                  type="button"
                  onClick={() => updateFormData({ texture: texture.url })}
                  className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    formData.texture === texture.url
                      ? "border-teal-500 ring-2 ring-teal-500 ring-offset-2"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: texture.url
                        ? `url(${getCloudflareImageUrl(texture.url, "format=auto,width=150")})`
                        : "none",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundColor: texture.url ? "transparent" : formData.bgColor,
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                    <span className="text-xs text-white font-medium">{texture.name}</span>
                  </div>
                  {formData.texture === texture.url && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {!formData.texture && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Pozadinska boja
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.bgColor}
                  onChange={(e) => updateFormData({ bgColor: e.target.value })}
                  className="w-12 h-12 rounded-lg border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.bgColor}
                  onChange={(e) => updateFormData({ bgColor: e.target.value })}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "colors" && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Brzi odabir teme
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {colorPresets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => applyColorPreset(preset)}
                  className="p-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-all"
                >
                  <div className="flex gap-1 mb-2">
                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: preset.bg }} />
                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: preset.highlight }} />
                    <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: preset.text }} />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Boja naslova
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.titleColor}
                  onChange={(e) => updateFormData({ titleColor: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.titleColor}
                  onChange={(e) => updateFormData({ titleColor: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Boja naglaska
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.highlightColor}
                  onChange={(e) => updateFormData({ highlightColor: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.highlightColor}
                  onChange={(e) => updateFormData({ highlightColor: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "typography" && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font naslova
            </label>
            <select
              value={formData.titleFont}
              onChange={(e) => updateFormData({ titleFont: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {fonts.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.name}
                </option>
              ))}
            </select>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <span className={`text-2xl font-${formData.titleFont}`} style={{ color: formData.titleColor }}>
                {formData.eventName || "Primjer teksta"}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Font teksta
            </label>
            <select
              value={formData.textFont}
              onChange={(e) => updateFormData({ textFont: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {fonts.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Veličina naslova
            </label>
            <input
              type="range"
              min="24"
              max="64"
              value={formData.titleFontSize}
              onChange={(e) => updateFormData({ titleFontSize: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>Mala</span>
              <span>{formData.titleFontSize}px</span>
              <span>Velika</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === "image" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Naslovna slika
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.includeHeaderImage}
                onChange={(e) => updateFormData({ includeHeaderImage: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
            </label>
          </div>

          {formData.includeHeaderImage && (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6">
              {formData.headerImage ? (
                <div className="relative">
                  <img
                    src={formData.headerImage}
                    alt="Header"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => updateFormData({ headerImage: "" })}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center cursor-pointer">
                  {isUploadingHeader ? (
                    <div className="flex flex-col items-center">
                      <svg className="animate-spin h-10 w-10 text-teal-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="mt-2 text-sm text-gray-500">Učitavanje...</span>
                    </div>
                  ) : (
                    <>
                      <svg className="w-10 h-10 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">Kliknite za učitavanje slike</span>
                      <span className="text-xs text-gray-500 mt-1">PNG, JPG do 5MB</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isUploadingHeader}
                  />
                </label>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StepCustomizeDesign;
